// 合法动作枚举：UI 高亮 + AI 决策共用
// 产出的对象即 GameEngine.apply 可直接消费的 EngineAction（play-card 含完整可选字段）
import type { GameState, PlayerState } from "@/types/game";
import { type EngineAction, type EquipSlot, getCatalogEntry, accessoryTargetBlockReason, findSubstituteConfig } from "./GameEngine";
import type { ResponseKind, DeckCard, Suit } from "@/types/cards";
import { targetsInRange } from "./distance";
import { canRespondWith } from "./Resolver";
import { canPickFromDiscard } from "./Deck";
import { responseKindOfCard, parseSkillDamage, parseSupplyHeal } from "./effectParse";
import {
  isFrozen,
  isSilenced,
  isEffectLocked,
  isAttackLocked,
  hasBloodlust,
} from "./Status";
import { getPassive, getActiveSkill } from "./skills/registry";
import {
  equipSlotOf,
  effectiveHandLimit,
  isSlotProtectedByJingang,
  equipManaCostReduction,
} from "./Equipment";

export type { EngineAction as LegalAction };

export interface EnumerateOptions {
  // 非当前执行玩家：仅枚举急救动作（濒死队友治疗）。默认 true
  rescueOnlyForOthers?: boolean;
}

function find(state: GameState, playerId: string): PlayerState | undefined {
  return state.players.find((p) => p.id === playerId);
}

// 主行动（攻/法/摄魂）是否还能打出：考虑嗜血/连刺/狂风之刃/诛神之剑
// 与 GameEngine.requireMainActionAvailable 保持一致，否则 UI 不高亮但引擎允许
function canUseMainAction(
  player: PlayerState,
  state: GameState,
  kind: "攻击牌" | "法术牌" | "摄魂牌"
): boolean {
  if (!player.hasPlayedAttackThisTurn) return true;
  // 嗜血：本执行阶段可出任意张攻击/法术（摄魂除外）
  if (hasBloodlust(player) && kind !== "摄魂牌") return true;
  // 狂风之刃：每个执行阶段可使用2张攻击/法术/摄魂牌
  if (
    player.equip.weapon?.name === "狂风之刃" &&
    state.turnFlags.attacksPlayed < 2
  ) {
    return true;
  }
  if (kind === "攻击牌") {
    const max = getPassive(player.hero.name).maxAttacksPerTurn ?? 1;
    if (state.turnFlags.attacksPlayed < max) return true; // 连刺
    // 诛神之剑：攻击牌未造成伤害时，可再打出1张攻击牌
    if (
      player.equip.weapon?.name === "诛神之剑" &&
      state.turnFlags.zhushenComboAvailable
    ) {
      return true;
    }
  }
  return false;
}

type PlayCardAction = Extract<EngineAction, { type: "play-card" }>;

function playCardAction(
  card: DeckCard,
  extra: Partial<PlayCardAction> = {}
): PlayCardAction {
  return { type: "play-card", playerId: "", cardUid: card.uid, ...extra };
}

// 某玩家视角的合法动作（playerId 缺省 = 当前玩家）
export function enumerateLegalActions(
  state: GameState,
  playerId?: string,
  opts: EnumerateOptions = {}
): EngineAction[] {
  const rescueOnlyForOthers = opts.rescueOnlyForOthers ?? true;
  const current = state.players[state.currentPlayerIdx];
  const player = playerId ? find(state, playerId) : current;
  // 濒死玩家不可行动（需等待结算：救援或死亡）
  if (!player || !player.isAlive || player.isDying) return [];
  const isCurrent = current.id === player.id;

  switch (state.phase) {
    case "draw":
      return isCurrent ? [{ type: "draw" }] : [];

    case "play": {
      // 非当前玩家：仅允许急救（濒死队友治疗）
      if (!isCurrent && rescueOnlyForOthers) {
        return rescueActions(state, player);
      }

      const actions: EngineAction[] = [];
      const frozen = isFrozen(player);

      for (const card of player.hand) {
        const base = playCardAction(card, { playerId: player.id });

        switch (card.kind) {
          case "攻击牌": {
            if (frozen || isAttackLocked(player)) break;
            if (!canUseMainAction(player, state, "攻击牌")) break;
            // 龙鳞：红色攻击牌无法指定佩戴者
            const rangeTargets = targetsInRange(state.players, player.id)
              .map((id) => state.players.find((p) => p.id === id)!)
              .filter((t) => !accessoryTargetBlockReason(t, card));
            for (const t of rangeTargets) {
              actions.push({ ...base, targetId: t.id });
            }
            // 呼神项链：可指定2个目标（枚举全部两两组合）
            if (player.equip.accessory?.name === "呼神项链") {
              for (let i = 0; i < rangeTargets.length; i++) {
                for (let j = i + 1; j < rangeTargets.length; j++) {
                  actions.push({
                    ...base,
                    targetId: rangeTargets[i].id,
                    targetId2: rangeTargets[j].id,
                  });
                }
              }
            }
            break;
          }
          case "法术牌": {
            if (frozen || isSilenced(player)) break;
            if (!canUseMainAction(player, state, "法术牌")) break;
            actions.push(...spellActions(state, player, card));
            break;
          }
          case "摄魂牌": {
            if (frozen || !canUseMainAction(player, state, "摄魂牌")) break;
            for (const t of enemies(state, player)) {
              actions.push({ ...base, targetId: t.id });
            }
            break;
          }
          case "公共法术牌": {
            if (frozen || isSilenced(player)) break;
            actions.push(...publicSpellActions(state, player, card));
            break;
          }
          case "补给牌": {
            if (frozen) break;
            for (const t of aliveAllies(state, player)) {
              actions.push({ ...base, targetId: t.id });
            }
            break;
          }
          case "装备牌": {
            if (frozen || !isCurrent) break;
            if (equipSlotOf(card)) actions.push(base);
            break;
          }
          case "效果牌": {
            if (frozen || isEffectLocked(player)) break;
            actions.push(...activeEffectActions(state, player, card, isCurrent));
            break;
          }
          default:
            break;
        }
      }

      // 卡牌替换（掠夺之甲→掠夺 / 杀戮之戒→PG攻击 / 狂风项链→HG攻击）
      if (!frozen && isCurrent) {
        const sub = findSubstituteConfig(player, "active");
        if (sub && player.hand.length >= 2) {
          if (sub.config.cardName === "掠夺") {
            // 掠夺：枚举敌方 + 每个有装备的槽位
            for (const t of enemies(state, player)) {
              if (t.equip.armor?.name === "掠夺之甲") continue;
              for (const slot of ["weapon", "armor", "accessory"] as EquipSlot[]) {
                if (t.equip[slot] && !isSlotProtectedByJingang(t, slot)) {
                  actions.push({
                    type: "play-card",
                    playerId: player.id,
                    cardUid: -1,
                    substituteCardUids: [-1, -1], // 占位：UI/AI 负责填入选中的2张牌
                    substituteAs: "掠夺",
                    targetId: t.id,
                    slot,
                  });
                }
              }
            }
          } else if (sub.config.cardName === "PG攻击" || sub.config.cardName === "HG攻击") {
            // PG/HG攻击：枚举射程内敌方目标
            const cardKind = sub.config.cardName; // "PG攻击" or "HG攻击"
            if (!isAttackLocked(player) && canUseMainAction(player, state, "攻击牌")) {
              for (const tid of targetsInRange(state.players, player.id)) {
                const t = state.players.find((p) => p.id === tid)!;
                // 龙鳞：红色攻击牌无法指定（PG攻击是红，HG攻击是红）
                if (accessoryTargetBlockReason(t, { kind: "攻击牌", suit: "红", name: cardKind } as DeckCard)) continue;
                actions.push({
                  type: "play-card",
                  playerId: player.id,
                  cardUid: -1,
                  substituteCardUids: [-1, -1],
                  substituteAs: cardKind,
                  targetId: tid,
                });
              }
            }
          }
        }
      }

      if (isCurrent) actions.push({ type: "end-turn" });
      return actions;
    }

    case "discard": {
      if (!isCurrent) return [];
      const overflow = player.hand.length - effectiveHandLimit(player);
      if (overflow <= 0) return [{ type: "end-turn" }];
      // 具体弃哪些由 AI/UI 决定；此处给出一个需要弃牌的标记动作（弃全部），
      // 调用方应改用 buildDiscardAction 选择最低价值的 N 张
      return [
        {
          type: "discard",
          uids: player.hand.map((c) => c.uid),
        },
      ];
    }

    case "response": {
      if (!state.responseWindow) return [];
      const w = state.responseWindow;
      if (!w.responders.includes(player.id)) return [];
      const actions: EngineAction[] = [];
      for (const card of player.hand) {
        const rk = responseKindOfCard(card.name);
        if (rk && canRespondWith(state, player.id, rk as ResponseKind)) {
          actions.push({
            type: "respond",
            playerId: player.id,
            cardUid: card.uid,
            responseKind: rk as ResponseKind,
          });
        }
      }
      // 替换响应（弃鬼盾→解除 / 狂风刃甲→反射）
      const sub = findSubstituteConfig(player, "respond");
      if (sub && canRespondWith(state, player.id, sub.config.cardName as ResponseKind)) {
        const blackCount = player.hand.filter((c) => c.suit === "黑").length;
        const needBlack = sub.config.suitFilter === "黑";
        if (needBlack ? blackCount >= 2 : player.hand.length >= 2) {
          actions.push({
            type: "respond",
            playerId: player.id,
            cardUid: -1,
            substituteCardUids: [-1, -1], // 占位：UI/AI 填入
            responseKind: sub.config.cardName as ResponseKind,
          });
        }
      }
      actions.push({ type: "pass-response", playerId: player.id });
      return actions;
    }

    case "pickup-response": {
      // 拾遗窗口：仅当前待决策玩家可行动，可对本批新弃牌中任一张使用拾遗
      const w = state.pickupWindow;
      if (!w || !w.responders.includes(player.id)) return [];
      if (w.decisions[player.id] !== "pending") return [];
      const pickupCard = player.hand.find(
        (c) => c.name === "拾遗" && c.kind === "效果牌"
      );
      if (!pickupCard) return [];
      const actions: EngineAction[] = w.cards.map((c) => ({
        type: "pickup",
        playerId: player.id,
        cardUid: pickupCard.uid,
        targetUid: c.uid,
      }));
      actions.push({ type: "pass-pickup", playerId: player.id });
      return actions;
    }

    default:
      return [];
  }
}

// 法术牌 → 英雄主动技能候选（含目标模式 single/all-enemies/all-allies/redeem）
function spellActions(
  state: GameState,
  player: PlayerState,
  card: DeckCard
): EngineAction[] {
  const out: EngineAction[] = [];
  for (const skill of player.hero.skills) {
    if (skill.type !== "active") continue;
    // 法神戒指：魔法消耗-5（不低于0）
    const manaCost = Math.max(0, (skill.manaCost ?? 0) - equipManaCostReduction(player));
    if (player.mp < manaCost) continue;
    const def = getActiveSkill(skill.name);
    const base: EngineAction = {
      type: "play-card",
      playerId: player.id,
      cardUid: card.uid,
      skillName: skill.name,
    };

    if (def?.targetMode === "all-enemies" || def?.targetMode === "all-allies") {
      out.push(base);
      continue;
    }
    if (def?.targetMode === "redeem") {
      // 友军治疗 / 敌军伤害均可（敌军伤害受凤羽免疫限制）
      for (const t of state.players) {
        if (!t.isAlive || t.id === player.id) continue;
        if (t.team !== player.team && accessoryTargetBlockReason(t, card)) continue;
        out.push({ ...base, targetId: t.id });
      }
      // 也允许对自己施放（治疗）
      out.push({ ...base, targetId: player.id });
      continue;
    }
    // 单体：需要可解析伤害
    if (!parseSkillDamage(skill.description)) continue;
    for (const t of enemies(state, player)) {
      // 凤羽：蓝色法术牌无法指定佩戴者
      if (accessoryTargetBlockReason(t, card)) continue;
      out.push({ ...base, targetId: t.id });
    }
  }
  return out;
}

// 公共法术牌候选
function publicSpellActions(
  state: GameState,
  player: PlayerState,
  card: DeckCard
): EngineAction[] {
  const base: EngineAction = {
    type: "play-card",
    playerId: player.id,
    cardUid: card.uid,
  };
  switch (card.name) {
    case "贪婪":
    case "嗜血":
      return [base]; // 非指向，直接结算
    case "命运链接": {
      // 指定 2 名相邻存活玩家（环形座位 0-1-2-3-0）
      const pairs: PlayCardAction[] = [];
      for (let i = 0; i < 4; i++) {
        const a = state.players[i];
        const b = state.players[(i + 1) % 4];
        if (a.isAlive && b.isAlive) {
          pairs.push({ ...base, targetId: a.id, targetId2: b.id });
        }
      }
      return pairs;
    }
    case "飞龙探云手": {
      // 卡面：抽目标1张手牌——玩家背面盲选位置（每手牌一个动作，UI 不展示牌面）
      const out: EngineAction[] = [];
      for (const t of enemies(state, player)) {
        for (const c of t.hand) {
          out.push({ ...base, targetId: t.id, stealCardUid: c.uid });
        }
      }
      return out;
    }
    case "净化":
    case "致命弱点":
    case "无间炼狱":
    case "沉默":
      // 净化可指向任意活人；其余指向敌人
      return targetsFor(state, player, card.name === "净化" ? "any-alive" : "enemies")
        .map((t) => ({ ...base, targetId: t.id }));
    case "杀戮之心": {
      // 卡面：目标弃去指定花色所有手牌——玩家盲选花色，不得泄露目标手牌构成
      // 对每个敌方目标恒枚举红/黑/蓝三色；选到目标没有的花色即弃 0 张，
      // AI 靠一 ply 前瞻自然选弃牌最多的花色
      const suits: NonNullable<Suit>[] = ["红", "黑", "蓝"];
      const out: EngineAction[] = [];
      for (const t of enemies(state, player)) {
        for (const suit of suits) {
          out.push({ ...base, targetId: t.id, suit });
        }
      }
      return out;
    }
    default:
      return [];
  }
}

// 主动类效果牌候选：缴械/掠夺/拾遗（封杀一期 no-op，不枚举）
function activeEffectActions(
  state: GameState,
  player: PlayerState,
  card: DeckCard,
  isCurrent: boolean
): EngineAction[] {
  const base: EngineAction = {
    type: "play-card",
    playerId: player.id,
    cardUid: card.uid,
  };
  switch (card.name) {
    case "缴械":
    case "掠夺": {
      if (!isCurrent) return [];
      // 卡面：弃/夺目标一件「指定」装备 → 对每件已装备的装备各枚举一个动作（带 slot）
      // 掠夺之甲：无法成为掠夺的目标
      const slots: EquipSlot[] = ["weapon", "armor", "accessory"];
      const out: EngineAction[] = [];
      for (const t of enemies(state, player)) {
        if (card.name === "掠夺" && t.equip.armor?.name === "掠夺之甲") continue;
        for (const slot of slots) {
          if (!t.equip[slot]) continue;
          // 金刚戒指：武器/防具槽受保护（戒指自身所在饰品槽仍可指定）
          if (isSlotProtectedByJingang(t, slot)) continue;
          out.push({ ...base, targetId: t.id, slot });
        }
      }
      return out;
    }
    case "拾遗": {
      // 只能取判定牌/被动弃牌（自己打出的、回合结束弃的不可取）
      // 无限制：任何玩家 play 阶段可用；AI 仅枚举合规牌顶部若干张
      const eligible = state.discardPile.filter(canPickFromDiscard);
      const top = eligible.slice(-5);
      return top.map((c) => ({ ...base, takeFromDiscardUid: c.uid }));
    }
    default:
      return []; // 响应类效果牌不能主动打出
  }
}

// 判断补给牌能否把濒死目标救回：必须能恢复生命（hp>0）
// 纯回蓝（魔法药水）/无恢复效果（金币）无法救人，不进救援列表
export function supplyHealHp(state: GameState, card: DeckCard): number {
  const catalog = getCatalogEntry(state, card.id);
  if (!catalog) return 0;
  return parseSupplyHeal(catalog.effectText).hp ?? 0;
}

// 非当前玩家的急救动作：对濒死队友使用能恢复生命的治疗补给
function rescueActions(state: GameState, player: PlayerState): EngineAction[] {
  const dyingAllies = state.players.filter(
    (p) => p.team === player.team && p.isAlive && p.isDying
  );
  if (dyingAllies.length === 0) return [];
  const frozen = isFrozen(player);
  const out: EngineAction[] = [];
  for (const card of player.hand) {
    if (card.kind !== "补给牌" || frozen) continue;
    // 只列真正能救命的牌（恢复生命>0，如生命药水/复活药水；排除纯回蓝与金币）
    if (supplyHealHp(state, card) <= 0) continue;
    for (const t of dyingAllies) {
      out.push({
        type: "play-card",
        playerId: player.id,
        cardUid: card.uid,
        targetId: t.id,
      });
    }
  }
  return out;
}

function targetsFor(
  state: GameState,
  player: PlayerState,
  mode: "enemies" | "any-alive"
): PlayerState[] {
  if (mode === "enemies") return enemies(state, player);
  return state.players.filter((p) => p.isAlive);
}

function enemies(state: GameState, player: PlayerState): PlayerState[] {
  return state.players.filter((p) => p.team !== player.team && p.isAlive);
}

function aliveAllies(state: GameState, player: PlayerState): PlayerState[] {
  return state.players.filter(
    (p) => p.team === player.team && p.isAlive
  );
}

// 弃牌阶段需要弃掉的最少数量
export function requiredDiscardCount(player: PlayerState): number {
  return Math.max(0, player.hand.length - effectiveHandLimit(player));
}

// 构造弃牌动作（供 AI/UI 选定具体 uid 后使用）
export function buildDiscardAction(uids: number[]): EngineAction {
  return { type: "discard", uids };
}

export { targetsFor };
